import { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from './apiFetch';

/**
 * usePincodeCheck
 * Calls the real serviceability API —
 *   GET /api/v1/customer/check-serviceability?pincode=XXXXXX&product_id=XX[&product_variant_id=YY]
 *
 * Returns:
 *   status              — 'idle' | 'checking' | 'serviceable' | 'not_serviceable' | 'error'
 *   message             — Human-readable result message
 *   data                — Full API response data or null
 *   productAvailability — product_availability object from API or null
 *   stockLevel          — 'OUT_OF_STOCK'|'LOW_STOCK'|'LIMITED_STOCK'|'IN_STOCK'|null
 *   availableStock      — Net available_stock number from the API, or null
 *   checkNow            — Imperative function to force a check for a given pincode
 */
const usePincodeCheck = (pincode, productId = null, variantId = null) => {
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [data, setData] = useState(null);
  const [productAvailability, setProductAvailability] = useState(null);
  const [checkedPincode, setCheckedPincode] = useState('');
  const timerRef = useRef(null);
  const abortRef = useRef(null);

  const check = useCallback(async (pin) => {
    if (!pin || pin.length !== 6 || /\D/.test(pin)) {
      setCheckedPincode('');
      setStatus('idle');
      setMessage('');
      setData(null);
      return;
    }

    // Cancel any in-flight request
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setCheckedPincode(pin);
    setStatus('checking');
    setMessage('');
    setData(null);
    setProductAvailability(null);

    try {
      // Build query string — include product_id and optionally product_variant_id
      const params = new URLSearchParams({ pincode: pin });
      if (productId) params.set('product_id', String(productId));
      if (variantId)  params.set('product_variant_id', String(variantId));
      const qs = `api/v1/customer/check-serviceability?${params.toString()}`;

      const json = await apiFetch(qs, { method: 'GET', auth: false });

      if (controller.signal.aborted) return;

      const result = json.data ?? {};
      setData(result);
      setProductAvailability(result.product_availability ?? null);

      if (result.serviceable || result.delivery_available) {
        setStatus('serviceable');
        setMessage(
          json.message ||
          `Delivery available — ${result.area_name ? `${result.area_name}, ` : ''}${result.city_name || 'Your area'}${result.state ? `, ${result.state}` : ''}`,
        );
      } else {
        setStatus('not_serviceable');
        setMessage(json.message || 'Delivery not available for this pincode. Coming soon!');
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      const response = err.details;
      const responseData = response?.data ?? null;
      const unavailable = responseData &&
        responseData.serviceable === false &&
        responseData.delivery_available === false;
      setStatus(unavailable ? 'not_serviceable' : 'error');
      setMessage(response?.message || err.message || 'Unable to check serviceability. Please try again.');
      setData(responseData);
      setProductAvailability(null);
    }
  }, [productId, variantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!pincode || pincode.length !== 6) {
      setCheckedPincode('');
      setStatus('idle');
      setMessage('');
      setData(null);
      setProductAvailability(null);
      return;
    }
    // Drop the previous pincode's result immediately so it cannot be shown or
    // used while the next serviceability request is waiting for its debounce.
    setCheckedPincode(pincode);
    setStatus('checking');
    setMessage('');
    setData(null);
    setProductAvailability(null);
    // Debounce 400ms so we don't fire on every keystroke
    timerRef.current = setTimeout(() => check(pincode), 400);
    return () => {
      clearTimeout(timerRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [pincode, check]);

  // Derived convenience fields from productAvailability
  const stockLevel     = productAvailability?.stock_status ?? productAvailability?.stock_level ?? null;
  const availableStock = productAvailability?.available_qty ?? productAvailability?.available_stock ?? null;

  return { status, message, data, productAvailability, stockLevel, availableStock, checkedPincode, checkNow: check };
};

export default usePincodeCheck;
